// DetailPage — detail-page composer (CHG-1225).
//
// Consumes a DetailPageConfig from @generated/screens/<id>. Renders:
//   PageHeader (with backTo + title-from-record + optional stage-chip badge)
//   → optional StatCards row (driven from config.statusCards)
//   → tab nav → active tab content
//
// Tabs declare {id, label, render(record, formatters)}. Tab state lives
// inside this primitive; consumers never useState for it.
//
// Per .forge/plans/v2-canary-pages.md §1.1 + dashboard-v2-stage-treatments.md.
//
// CHG-1292 (minor-version bump, 2 of 4 in the audit-approved primitive
// extension sequence): DetailPageTab.permission (CHG-1225 placeholder) is
// now a real RBAC gate. When at least one tab declares a permission, the
// DetailPage delegates rendering to GatedDetailPage which calls
// usePermissions and filters the visible tabs. When NO tab declares a
// permission, the legacy PlainDetailPage path runs — no permission hooks
// fire, so existing test fixtures without a QueryClientProvider continue
// to work unchanged. Header-action gating already flows through
// PageHeader's CHG-1291 permissionGate field — config.actions() returns
// PageHeaderAction[] whose entries may carry their own gate.

import { type ReactNode, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { PageHeader, type PageHeaderAction } from "./PageHeader";
import { StatCards, type StatCardDef } from "./StatCards";
import { useFormatters, type Formatters } from "../../contexts/IntlContext";
import { usePermissions } from "../../auth/usePermissions";

export interface DetailPageTab<T> {
  id: string;
  label: string;
  /** CHG-1225 declared this field; CHG-1292 wires it as a real RBAC gate:
   * tabs with a permission the user lacks are hidden from the tab strip.
   * Active-tab fallback selects the first visible tab. Record-aware
   * action gating flows through PageHeader's CHG-1291 mechanism. */
  permission?: string;
  render: (record: T, formatters: Formatters) => ReactNode;
}

export interface DetailPageConfig<T> {
  /** URL param name carrying the record id (e.g. "donorId"). */
  idParam: string;
  /** react-query key + fetch fn for the primary record. */
  queryKey: (id: string) => readonly unknown[];
  queryFn: (id: string) => Promise<T>;
  /** Where the back arrow points (e.g. "/donors"). */
  backTo: string;
  /** Title from the record (e.g. row => row.full_name). */
  title: (record: T) => string;
  subtitle?: (record: T) => ReactNode;
  /** Stage chip badge in the header (optional). */
  stageChip?: (record: T) => ReactNode;
  /** Right-aligned action buttons (record-aware). */
  actions?: (record: T) => PageHeaderAction[];
  /** Status cards row (record-aware). */
  statusCards?: (record: T, fmt: Formatters) => StatCardDef[];
  /** Optional page-level custom-rendered sections.
   *
   * CHG-1665 / ITER-3774 — slot for entity-specific inline panels that
   * don't fit the statusCards row or the tab strip (e.g. address card,
   * annual_report_inline). Rendered between statusCards and tabs.
   * Each section's `render` callback receives the record + formatters
   * and returns a ReactNode. v2gen.py emits per-section override-blocks
   * (`override-block:section-<id>`) from `pages.detail_v2.sections`. */
  sections?: Array<{
    id: string;
    render: (record: T, fmt: Formatters) => ReactNode;
  }>;
  /** Tabs. */
  tabs: DetailPageTab<T>[];
}

export interface DetailPageProps<T> {
  config: DetailPageConfig<T>;
}

export function DetailPage<T>({ config }: DetailPageProps<T>) {
  // Pure-routing decision: if any tab declares a permission, take the
  // gated path (which calls usePermissions). Otherwise take the plain
  // path. Branch is per-config and stable across renders for a given
  // page, so neither branch toggles between renders — both subcomponents
  // are full DetailPage implementations and pick the appropriate hook set.
  const hasAnyGate = config.tabs.some((t) => !!t.permission);
  return hasAnyGate ? (
    <GatedDetailPage config={config} />
  ) : (
    <PlainDetailPage config={config} />
  );
}

// ─────────────────────────────────────────────────────────────────
// Plain DetailPage — no permission hooks. Used when no tab declares
// a permission. Identical to the pre-CHG-1292 implementation.
// ─────────────────────────────────────────────────────────────────

function PlainDetailPage<T>({ config }: DetailPageProps<T>) {
  const ctx = useDetailPageQuery(config);
  if (ctx.kind !== "ready") return ctx.render;
  const { record, activeTab, activeTabId, setActiveTabId, fmt } = ctx;
  return (
    <DetailPageChrome
      config={config}
      record={record}
      fmt={fmt}
      visibleTabs={config.tabs}
      activeTab={activeTab}
      activeTabId={activeTabId}
      setActiveTabId={setActiveTabId}
    />
  );
}

// ─────────────────────────────────────────────────────────────────
// Gated DetailPage — calls usePermissions to filter visible tabs.
// Only mounted when at least one tab has a permission declared.
// ─────────────────────────────────────────────────────────────────

function GatedDetailPage<T>({ config }: DetailPageProps<T>) {
  const ctx = useDetailPageQuery(config);
  const perms = usePermissions();
  const visibleTabs = useMemo(() => {
    return config.tabs.filter((t) => {
      if (!t.permission) return true;
      if (!perms.data) return false;          // fail closed during loading
      return perms.data.includes(t.permission);
    });
  }, [config.tabs, perms.data]);
  if (ctx.kind !== "ready") return ctx.render;
  const { record, activeTabId, setActiveTabId, fmt } = ctx;
  const activeTab =
    visibleTabs.find((t) => t.id === activeTabId) ?? visibleTabs[0];
  return (
    <DetailPageChrome
      config={config}
      record={record}
      fmt={fmt}
      visibleTabs={visibleTabs}
      activeTab={activeTab}
      activeTabId={activeTabId}
      setActiveTabId={setActiveTabId}
    />
  );
}

// ─────────────────────────────────────────────────────────────────
// Shared query + early-return state machine. Returns either a
// ready-record context or a JSX node to return directly (loading /
// error / missing-id).
// ─────────────────────────────────────────────────────────────────

type QueryCtx<T> =
  | { kind: "render"; render: ReactNode }
  | {
      kind: "ready";
      record: T;
      fmt: Formatters;
      activeTab: DetailPageTab<T> | undefined;
      activeTabId: string;
      setActiveTabId: (id: string) => void;
      render: ReactNode;
    };

function useDetailPageQuery<T>(config: DetailPageConfig<T>): QueryCtx<T> {
  const params = useParams();
  const fmt = useFormatters();
  const id = params[config.idParam];
  const [activeTabId, setActiveTabId] = useState<string>(config.tabs[0]?.id ?? "");

  const query: UseQueryResult<T> = useQuery({
    queryKey: id ? config.queryKey(id) : [],
    queryFn: id ? () => config.queryFn(id) : undefined,
    enabled: !!id,
  });

  if (!id) {
    return { kind: "render", render: <main style={{ padding: 24 }}>Missing :id parameter.</main> };
  }
  if (query.isLoading) {
    return {
      kind: "render",
      render: (
        <main style={{ maxWidth: 1280, margin: "0 auto", padding: "16px 24px 48px" }}>
          <PageHeader title="Loading…" backTo={config.backTo} />
        </main>
      ),
    };
  }
  if (query.isError) {
    return {
      kind: "render",
      render: (
        <main style={{ maxWidth: 1280, margin: "0 auto", padding: "16px 24px 48px" }}>
          <PageHeader title="Error" backTo={config.backTo} />
          <div className="card" style={{ padding: 24, color: "var(--color-error)" }}>
            {(query.error as Error).message}
          </div>
        </main>
      ),
    };
  }
  const record = query.data as T;
  const activeTab = config.tabs.find((t) => t.id === activeTabId) ?? config.tabs[0];
  return {
    kind: "ready",
    record,
    fmt,
    activeTab,
    activeTabId,
    setActiveTabId,
    render: null,
  };
}

// ─────────────────────────────────────────────────────────────────
// Shared chrome — header + status cards + tab strip + active panel.
// Pure presentational; both DetailPage variants render through this.
// ─────────────────────────────────────────────────────────────────

interface DetailPageChromeProps<T> {
  config: DetailPageConfig<T>;
  record: T;
  fmt: Formatters;
  visibleTabs: DetailPageTab<T>[];
  activeTab: DetailPageTab<T> | undefined;
  activeTabId: string;
  setActiveTabId: (id: string) => void;
}

function DetailPageChrome<T>({
  config,
  record,
  fmt,
  visibleTabs,
  activeTab,
  setActiveTabId,
}: DetailPageChromeProps<T>) {
  return (
    <main style={{ maxWidth: 1280, margin: "0 auto", padding: "16px 24px 48px" }}>
      <PageHeader
        title={config.title(record)}
        subtitle={config.subtitle?.(record)}
        backTo={config.backTo}
        badges={config.stageChip?.(record)}
        actions={config.actions?.(record)}
      />
      {config.statusCards ? <StatCards cards={config.statusCards(record, fmt)} /> : null}

      {config.sections && config.sections.length > 0 ? (
        <div
          data-detail-sections
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            marginBottom: 20,
          }}
        >
          {config.sections.map((s) => (
            <div key={s.id} data-section-id={s.id}>
              {s.render(record, fmt)}
            </div>
          ))}
        </div>
      ) : null}

      <nav role="tablist" aria-label="Sections" style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--color-border-subtle)", marginBottom: 16 }}>
        {visibleTabs.map((t) => {
          const active = t.id === activeTab?.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              data-tab-id={t.id}
              data-permission-gate-require={t.permission}
              onClick={() => setActiveTabId(t.id)}
              style={{
                padding: "10px 14px",
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                color: active ? "var(--color-text)" : "var(--color-text-muted)",
                background: "transparent",
                border: "none",
                borderBottom: active ? "2px solid var(--color-primary)" : "2px solid transparent",
                cursor: "pointer",
                marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      <div role="tabpanel" data-active-tab={activeTab?.id}>
        {activeTab ? activeTab.render(record, fmt) : null}
      </div>
    </main>
  );
}
